import { Box } from "@mui/material"
import { DataGridPremium, GridColDef, GridRenderCellParams, gridClasses } from "@mui/x-data-grid-premium"

import type { BulkLabelBatch } from "@app/services/types"
import { BatchActionsCell } from "./BatchActionsCell"

type BatchDataGridProps = {
	batches: BulkLabelBatch[]
	loading?: boolean
	page: number
	pageSize: number
	totalRows: number
	sortBy?: string
	sortOrder?: "asc" | "desc"
	onPageChange: (page: number) => void
	onPageSizeChange: (pageSize: number) => void
	onSortChange: (field: string | undefined, order: "asc" | "desc" | undefined) => void
}

export const BatchDataGrid: React.FC<BatchDataGridProps> = ({
	batches,
	loading = false,
	page,
	pageSize,
	totalRows,
	sortBy,
	sortOrder,
	onPageChange,
	onPageSizeChange,
	onSortChange,
}) => {
	const columns: GridColDef[] = [
		{
			field: "date_created",
			headerName: "Date Created",
			flex: 1,
			maxWidth: 300,
			sortable: true,
			valueFormatter: (value: string) => {
				return new Date(value).toLocaleString("en-US", {
					year: "numeric",
					month: "long",
					day: "numeric",
					hour: "numeric",
					minute: "2-digit",
				})
			},
		},
		{
			field: "created_by_user_name",
			headerName: "Name",
			flex: 1,
			maxWidth: 160,
			sortable: false,
			disableColumnMenu: true,
		},
		{
			field: "bulk_label_item_names",
			headerName: "Items",
			flex: 2,
			sortable: false,
			disableColumnMenu: true,
		},
		{
			field: "actions",
			headerName: "",
			width: 80,
			sortable: false,
			disableColumnMenu: true,
			align: "right",
			renderCell: (params: GridRenderCellParams<BulkLabelBatch>) => (
				<BatchActionsCell bulkLabelBatchId={params.row.bulk_label_batch_id} />
			),
		},
	]

	return (
		<Box sx={{ width: "100%", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					width: "100%",
					minWidth: 0,
					height: 0,
				}}
			>
				<DataGridPremium
					rows={batches}
					columns={columns}
					getRowId={(row) => row.bulk_label_batch_id}
					loading={loading}
					paginationMode="server"
					sortingMode="server"
					pagination
					paginationModel={{ page: page - 1, pageSize }}
					onPaginationModelChange={(model) => {
						onPageChange(model.page + 1)
						onPageSizeChange(model.pageSize)
					}}
					sortModel={sortBy && sortOrder ? [{ field: sortBy, sort: sortOrder }] : []}
					onSortModelChange={(model) => {
						if (model.length === 0) {
							onSortChange(undefined, undefined)
						} else {
							const { field, sort } = model[0]
							onSortChange(field, sort as "asc" | "desc")
						}
					}}
					pageSizeOptions={[10, 25, 50, 100]}
					rowCount={totalRows}
					disableRowSelectionOnClick
					sx={{
						minWidth: 0,
						minHeight: 0,
						width: "100%",
						border: "none",
						"& .MuiDataGrid-columnSeparator": {
							display: "none",
						},
						// Prevent highlight on cells when clicked
						[`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
							outline: "transparent",
						},
						[`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: {
							outline: "none",
						},
					}}
				/>
			</Box>
		</Box>
	)
}

export default BatchDataGrid
